<script setup lang="ts">
import { useUserManagementStore } from '@/stores/userManagement';
import { useFormStore } from '@/stores/forms';
import { computed, ref } from 'vue';
import { UserModel, type IUserPermissions } from '@/models/user';
import MultiFormSelector from './MultiFormSelector.vue';
import Select from 'primevue/select';
import MultiSelect from 'primevue/multiselect';
import Checkbox from 'primevue/checkbox';
import { AlertDialog, ButtonType, CustomButton } from '@/components';
import { FormModel } from '@/models/form';
import { useStudyStore } from '@/stores/study';
import { useStudyGroupsStore } from '@/stores/studyGroups';
import { cloneDeep } from 'lodash';
import Dialog from 'primevue/dialog';

const props = defineProps({
  user: {
    type: UserModel,
    required: true
  },
  visible: {
    type: Boolean,
    required: true
  }
});
const studyStore = useStudyStore();

const pumpNotificationProfiles = ['Surgeon', 'Perfusionist'];
const selectedNotificationProfile = ref(
  props.user.notificationProfile
);

const onNotificationProfileChangeListener = () => {
  updatedUserPermissions.value.notificationProfile =
    selectedNotificationProfile.value;
};

const permissions: (keyof IUserPermissions)[] = [
  'export',
  'manageInvest',
  'editCases',
  'createStudy'
];

const initialUserPermissions = ref<IUserPermissions>(
  cloneDeep(props.user.permissions)
);

const updatedUserPermissions = ref(cloneDeep(props.user.permissions));

const studyGroupsStore = useStudyGroupsStore();
studyGroupsStore.ensureStudyGroupsLoaded();

const selectedGroupIds = ref<string[]>(
  props.user.permissions.groupIdentifiers ??
    props.user.groups?.map(group => group.identifier) ??
    []
);
initialUserPermissions.value.groupIdentifiers = [...selectedGroupIds.value];
updatedUserPermissions.value.groupIdentifiers = [...selectedGroupIds.value];

const onGroupChangeListener = () => {
  updatedUserPermissions.value.groupIdentifiers = selectedGroupIds.value;
};

const viewEntriesPermissions = ['View all entries', 'View own entries'];
const selectedViewEntryPermission = ref(
  props.user.permissions.viewEntries === true ?
    viewEntriesPermissions[0] :
    viewEntriesPermissions[1]
);

const getPermissionLabel = (permission: string) => {
  switch (permission) {
    case 'export':
      return 'Export data';

    case 'manageInvest':
      return 'Manage Study';

    case 'editCases':
      return 'Edit cases';

    case 'createStudy':
      return 'Create study';

    default:
      break;
  }
};

const checkboxPermissions = computed(() => {
  return permissions.map(permission => ({
    key: permission,
    label: getPermissionLabel(permission),
    checked: props.user.permissions[permission] === true
  }));
});

const selectedPermissions = ref(
  checkboxPermissions.value
    .filter(permission => permission.checked)
    .map(permission => permission.label)
);

const onCheckboxPermissionsChangeListener = () => {
  permissions.forEach(permission => {
    (updatedUserPermissions.value[permission] as boolean) =
      selectedPermissions.value.includes(getPermissionLabel(permission));
  });
};

const userManagementStore = useUserManagementStore();
const formStore = useFormStore();

const savePermissions = async (userStatus: string) => {
  if (userStatus === 'Activated') {
    await userManagementStore.patchUserPermissions(
      props.user.identifier,
      updatedUserPermissions.value
    );
  } else {
    await userManagementStore.patchInvitePermissions(
      props.user,
      updatedUserPermissions.value
    );
  }
  emit('onClose');
};

const reInviteUser = async () => {
  await userManagementStore.reInviteUserToStudy(props.user);
  emit('onClose');
};

const onViewFormsChangeListner = (updatedForms: FormModel[]) => {
  if (updatedForms.length === formStore.formList.length) {
    updatedUserPermissions.value.forms = true;
  } else {
    updatedUserPermissions.value.forms = updatedForms.map(
      form => form.base64Id
    ) as string[];
  }
};

const onEditFormsChangeListner = (updatedForms: FormModel[]) => {
  if (updatedForms.length === formStore.formList.length) {
    updatedUserPermissions.value.editForms = true;
  } else {
    updatedUserPermissions.value.editForms = updatedForms.map(
      form => form.base64Id
    ) as string[];
  }
};

const onViewEntriesChangeListener = () => {
  updatedUserPermissions.value.viewEntries =
    selectedViewEntryPermission.value === viewEntriesPermissions[0];
};

const emit = defineEmits(['onClose']);

const cancelUpdates = () => {
  emit('onClose');
};

const isPristine = computed(() => {
  const initial = initialUserPermissions.value;
  const updated = updatedUserPermissions.value;
  return (
    initial.editCases !== updated.editCases ||
    initial.export !== updated.export ||
    initial.manageInvest !== updated.manageInvest ||
    initial.createStudy !== updated.createStudy ||
    JSON.stringify(initial.forms) !== JSON.stringify(updated.forms) ||
    JSON.stringify(initial.editForms) !== JSON.stringify(updated.editForms) ||
    initial.notificationProfile !== updated.notificationProfile ||
    JSON.stringify(initial.groupIdentifiers) !==
      JSON.stringify(updated.groupIdentifiers) ||
    initial.viewEntries !== updated.viewEntries
  );
});

const openAlertDialog = ref(false);

const removeUserFromStudyAlert = () => {
  openAlertDialog.value = !openAlertDialog.value;
};

const removeUserFromStudy = async () => {
  if (props.user.status === 'Activated') {
    await userManagementStore.removeUserFromStudy(props.user.identifier);
  } else {
    await userManagementStore.deleteInvitedUserFromStudy(props.user);
  }
  removeUserFromStudyAlert();
};

const closeDialog = () => {
  emit('onClose');
};
</script>

<template>
  <Dialog :visible="visible"
    modal
    class="dialog"
    :draggable="false"
    @update:visible="closeDialog">
    <template #header>
      <h2 class="dialog-title">
        Update permissions for <span>{{ user.name }}</span>
      </h2>
    </template>
    <div class="expand-root">
      <div class="expand-content">
        <div class="expand-form">
          <div class="view-form">
            <div class="select-header">View forms</div>
            <MultiFormSelector :selectedFormsIds="user.formsPermissions as string[]"
              @onFormChangeListener="onViewFormsChangeListner"/>
          </div>
          <div class="edit-form">
            <div class="select-header">Add entries</div>
            <div>
              <MultiFormSelector :selectedFormsIds="user.editFormsPermissions as string[]"
                @onFormChangeListener="onEditFormsChangeListner"/>
            </div>
          </div>
          <div class="view-entries">
            <div class="select-header">View entries</div>
            <div>
              <Select v-model="selectedViewEntryPermission"
                placeholder="Select"
                @change="onViewEntriesChangeListener"
                :options="viewEntriesPermissions"/>
            </div>
          </div>
          <div class="group">
            <div class="select-header">Groups</div>
            <div>
              <MultiSelect v-model="selectedGroupIds"
                @change="onGroupChangeListener"
                placeholder="Select groups"
                optionLabel="name"
                optionValue="identifier"
                :loading="studyGroupsStore.isStudyGroupsLoading.isLoading"
                :options="studyGroupsStore.studyGroups"/>
            </div>
          </div>
          <div class="user-type"
            v-if="
              studyStore.selectedStudy?.flags?.customDataDictionary === 'PUMP'
            ">
            <div class="select-header">Notification profile</div>
            <div>
              <Select v-model="selectedNotificationProfile"
                @change="onNotificationProfileChangeListener"
                placeholder="Select"
                showClear
                :options="pumpNotificationProfiles"/>
            </div>
          </div>
        </div>
        <div class="expand-permissions">
          <div v-for="(permission, index) in checkboxPermissions"
            v-bind:key="index">
            <div class="checkbox-permissions">
              <label for="permission" class="permission-label">
                {{ permission.label }}
              </label>
              <Checkbox v-model="selectedPermissions"
                @change="onCheckboxPermissionsChangeListener"
                :inputId="permission.key"
                name="permission"
                :value="permission.label"
                class="permission-checkbox"/>
            </div>
          </div>
        </div>
      </div>
      <hr />
      <div class="expand-buttons">
        <div class="expand-buttons-left">
          <CustomButton :text="
              user.status === 'Activated' ? 'Remove from study' : 'Delete Invite'
            "
            @onClick="removeUserFromStudyAlert"
            :type="ButtonType.NEGATIVE_OUTLINED"/>
          <CustomButton text="Re-send Invite"
            :margin-left="16"
            @onClick="reInviteUser"
            :type="ButtonType.POSITIVE"
            v-if="user.status === 'Pending'"
            :loading="userManagementStore.isReInvitingUser.isLoading"/>
        </div>
        <div class="expand-buttons-right">
          <CustomButton text="Cancel"
            @onClick="cancelUpdates"
            :type="ButtonType.OUTLINED"/>
          <CustomButton text="Save"
            :margin-left="16"
            @onClick="user.status && savePermissions(user.status)"
            :type="ButtonType.POSITIVE"
            :disabled="!isPristine"
            :loading="userManagementStore.isSavingPermissions.isLoading"/>
        </div>
      </div>
    </div>

    <AlertDialog :visible="openAlertDialog"
      @onConfirm="removeUserFromStudy"
      @onClose="removeUserFromStudyAlert"
      :loading="userManagementStore.isRemovingUser.isLoading"
      title="Confirm User Removal">
      <template #bodyContent>
        <div>
          <div class="p-text-secondary">
            Are you sure you want to remove
            <span class="strong">{{ user.email }}</span> from the study?
          </div>
        </div>
      </template>
    </AlertDialog>
  </Dialog>
</template>

<style scoped lang="scss">
.expand-content {
  padding-top: 12px;
  padding-bottom: 12px;
  min-width: min(800px, 86vw);
}
.expand-form {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(210px, 1fr));
  gap: 16px;
}
.expand-form :deep(.p-select),
.expand-form :deep(.p-multiselect) {
  width: 100%;
}
.select-header {
  font-weight: 600;
  margin-bottom: 8px;
}
.expand-permissions {
  display: flex;
  flex-wrap: wrap;
  margin-top: 32px;
  gap: 16px 32px;
}
@media (max-width: 640px) {
  .expand-form {
    grid-template-columns: 1fr;
  }
}
.checkbox-permissions {
  display: flex;
  flex-direction: column;
  align-items: center;
}
.permission-checkbox {
  margin-top: 8px;
}
.permission-label {
  font-weight: 600;
}
hr {
  border-color: hsla(0, 0%, 0%, 0.1);
  margin: 0.75rem -1rem;
}
.expand-buttons {
  display: flex;
  justify-content: space-between;
}
.expand-buttons-right {
  display: flex;
}
.expand-buttons-left {
  display: flex;
}
.strong {
  font-weight: 600;
}
</style>
